import React from 'react';
import { MapPin, Waves, Castle, Sun, Sailboat, Utensils, Mountain, Camera } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { GUIDE_SLUGS, guideLabels, guideTitle } from '../i18n/guides/meta';
import { langPath } from '../utils/siteNav';

const iconMap = [Waves, Castle, Sailboat, Sun, Mountain, Utensils, Camera, Waves];
const distanceMap = ["1.8 km", "2.5 km", "12 km", "3.2 km", "8 km", "2 km", "15 km", "4 km"];
const keyMap = ['velikaPlaza', 'oldTown', 'adaBojana', 'malaPlaza', 'saskoLake', 'restaurants', 'valdanos', 'ladiesBeach'];

const ExploreUlcinj = () => {
  const { t, language } = useLanguage();
  const labels = guideLabels(language);

  return (
    <section id="explore" className="py-20 bg-gradient-to-b from-slate-50 to-white" data-testid="explore-ulcinj-section">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-14">
          <span className="text-amber-700 font-medium tracking-widest text-sm uppercase">
            {t('explore.subtitle')}
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-slate-800 mt-3 mb-4">
            {t('explore.title')}
          </h2>
          <p className="text-slate-600 max-w-3xl mx-auto text-base md:text-lg">
            {t('explore.description')}
          </p>
        </div>

        {/* Attractions Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {keyMap.map((key, index) => {
            const Icon = iconMap[index];
            const attraction = t(`explore.attractions.${key}`) || {};
            return (
              <article
                key={key}
                className="bg-white rounded-xl p-5 shadow-sm border border-slate-100 hover:shadow-lg hover:border-amber-200 transition-all duration-300 group"
                data-testid={`attraction-card-${index}`}
              >
                <div className="w-11 h-11 bg-amber-50 rounded-lg flex items-center justify-center mb-3 group-hover:bg-amber-100 transition-colors">
                  <Icon className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="text-slate-800 font-semibold text-sm leading-tight mb-2">
                  {attraction.name || key}
                </h3>
                <div className="flex items-center gap-1 text-xs text-amber-600 font-medium mb-2">
                  <MapPin className="w-3 h-3" />
                  <span>{distanceMap[index]} {t('explore.fromHotel')}</span>
                </div>
                <p className="text-slate-500 text-xs leading-relaxed">
                  {attraction.desc || ''}
                </p>
              </article>
            );
          })}
        </div>

        {/* SEO text block */}
        <div className="mt-14 bg-amber-50 rounded-2xl p-8 border border-amber-100">
          <h3 className="text-lg font-semibold text-slate-800 mb-3">
            {t('explore.whyTitle')}
          </h3>
          <p className="text-slate-600 text-sm leading-relaxed mb-3">
            {t('explore.whyText1')}
          </p>
          <p className="text-slate-600 text-sm leading-relaxed">
            {t('explore.whyText2')}
          </p>
        </div>

        {/* Travel guides */}
        <div className="mt-10" data-testid="explore-guides">
          <div className="flex items-end justify-between gap-4 mb-4">
            <h3 className="text-lg font-semibold text-slate-800">{labels.title}</h3>
            <Link to={langPath(language, 'guides')} className="text-sm text-amber-700 hover:text-amber-800 font-medium whitespace-nowrap" data-testid="explore-guides-all">{labels.more} →</Link>
          </div>
          <div className="flex flex-wrap gap-2">
            {GUIDE_SLUGS.map((slug) => (
              <Link key={slug} to={langPath(language, `guides/${slug}`)} className="text-sm bg-white border border-slate-200 hover:border-amber-400 hover:text-amber-700 text-slate-700 px-4 py-2 rounded-full transition-colors" data-testid={`explore-guide-${slug}`}>{guideTitle(language, slug)}</Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExploreUlcinj;
